import React, { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import { Box, Typography, Button, Drawer, IconButton } from '@mui/material';
import { Dashboard as DashboardIcon, Paid } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCreditBalance } from '../hooks/useCreditBalance';
import logoSymbol from '../assets/icons/logo-symbol.svg';
import { MessageSquare, FileText, FileSearch, CreditCard, Menu, X } from 'lucide-react';

const AuthModal = lazy(() => import('./auth/AuthModal'));

const menuItems = [
  { icon: MessageSquare, label: 'Právní orientace', path: '/#orientace', protected: false },
  { icon: FileText, label: 'Generování smluv', path: '/#generovani', protected: false },
  { icon: FileSearch, label: 'Analýza smluv', path: '/#analyza', protected: false },
  { icon: CreditCard, label: 'Ceník', path: '/#cenik', protected: false },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { creditBalance } = useCreditBalance();
  const location = useLocation();
  const [headerAuthOpen, setHeaderAuthOpen] = useState(false);
  const [headerAuthMode, setHeaderAuthMode] = useState<'login' | 'register'>('login');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Dynamic responsive: measure if desktop layout fits
  const headerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [showHamburger, setShowHamburger] = useState(true);

  const checkFit = useCallback(() => {
    const header = headerRef.current;
    const measure = measureRef.current;
    if (!header || !measure) return;
    setShowHamburger(measure.offsetWidth > header.clientWidth);
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const observer = new ResizeObserver(checkFit);
    observer.observe(header);
    checkFit();
    return () => observer.disconnect();
  }, [checkFit, isAuthenticated]);

  const handleProtectedRoute = (path: string) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      setHeaderAuthMode('login');
      setHeaderAuthOpen(true);
    }
  };

  const handleNavClick = (item: typeof menuItems[0]) => {
    setMobileOpen(false);
    if (item.protected) {
      handleProtectedRoute(item.path);
      return;
    }
    if (item.path.startsWith('/#')) {
      const anchor = item.path.slice(2);
      if (location.pathname === '/') {
        const el = document.getElementById(anchor);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      navigate(item.path);
      return;
    }
    navigate(item.path);
  };

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate('/');
  };

  const formatCredits = (value: number | null) => (
    value === null ? '—' : value.toLocaleString('cs-CZ')
  );

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path);

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: showHamburger ? '60px' : '72px',
      backgroundColor: '#FAF8F5',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: '1px solid #E4DFD7',
      overflow: 'hidden',
    }}>
      {/* Hidden measurement bar - always renders full desktop layout to measure needed width */}
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          whiteSpace: 'nowrap',
          width: 'max-content',
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
        }}
      >
        {/* Logo mock */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Box sx={{ width: 48, height: 48 }} />
          <Typography sx={{ fontFamily: '"Rajdhani", sans-serif', fontSize: '30px' }}>PravniBOT.cz</Typography>
        </Box>
        {/* Nav mock */}
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {menuItems.map(item => (
            <Box key={item.path} sx={{ display: 'flex', alignItems: 'center', px: '12px', py: 1 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
        {/* Auth mock */}
        <Box sx={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
          {isAuthenticated ? (
            <>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, px: 2.5, py: 1 }}>Přehled</Typography>
              <Box sx={{ width: 130, py: 1 }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, px: 2.5, py: 1 }}>Odhlásit se</Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '15px', fontWeight: 500, px: '14px', py: 1 }}>Přihlásit</Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, px: 2.5, py: 1 }}>Vyzkoušet zdarma</Typography>
            </>
          )}
        </Box>
      </Box>

      <Box ref={headerRef} sx={{
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 3, md: 'max(24px, calc((100% - 1200px) / 2))' }
      }}>
        {/* Logo */}
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={logoSymbol}
            alt="Logo"
            sx={{
              width: showHamburger ? '36px' : '48px',
              height: showHamburger ? '36px' : '48px',
              transition: 'all 0.2s ease',
            }}
            loading="lazy"
          />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'baseline' }}>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: showHamburger ? '24px' : '30px',
              fontWeight: 400,
              color: '#1F1F1F',
              letterSpacing: '-0.5px',
              lineHeight: 1,
              transition: 'font-size 0.2s ease',
            }}>
              Pravni
            </Typography>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: showHamburger ? '24px' : '30px',
              fontWeight: 700,
              color: '#1F1F1F',
              letterSpacing: '-0.5px',
              lineHeight: 1,
              transition: 'font-size 0.2s ease',
            }}>
              BOT
            </Typography>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: showHamburger ? '24px' : '30px',
              fontWeight: 400,
              color: '#1F1F1F',
              letterSpacing: '-0.5px',
              lineHeight: 1,
              transition: 'font-size 0.2s ease',
            }}>
              .cz
            </Typography>
          </Box>
        </Box>

        {/* Desktop Nav - only when everything fits */}
        {!showHamburger && (
          <Box
            component="nav"
            id="primary-navigation"
            aria-label="Hlavní navigace"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {menuItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Box
                  key={item.path}
                  onClick={() => handleNavClick(item)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNavClick(item);
                    }
                  }}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    color: active ? '#1C1A18' : '#57524C',
                    transition: 'color 0.15s ease',
                    '&:hover': { color: '#E72F0D' },
                  }}
                >
                  <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '15px',
                    fontWeight: active ? 600 : 500,
                    lineHeight: '20px',
                    color: 'inherit',
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          {!showHamburger && isAuthenticated && (
            <>
              <Button
                onClick={() => navigate('/dashboard')}
                startIcon={<DashboardIcon sx={{ fontSize: 18 }} />}
                sx={{
                  height: '40px',
                  px: 2.5,
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  color: isActive('/dashboard') ? '#FFFFFF' : '#1F1F1F',
                  background: isActive('/dashboard')
                    ? '#1F1F1F'
                    : 'linear-gradient(180deg, #FFFFFF 0%, #F7F7F7 100%)',
                  border: isActive('/dashboard') ? '1px solid #1F1F1F' : '1px solid #E5E5E5',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                    background: isActive('/dashboard')
                      ? '#1F1F1F'
                      : 'linear-gradient(180deg, #FFFFFF 0%, #EFEFEF 100%)'
                  },
                }}
              >
                Přehled
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 500, color: '#1F1F1F' }}>
                  {user?.username}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Paid sx={{ fontSize: '16px', color: '#e72f0d' }} />
                  <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#666666' }}>
                    {formatCredits(creditBalance)} kreditů
                  </Typography>
                </Box>
              </Box>
              <Button
                onClick={handleLogout}
                sx={{
                  height: '40px',
                  padding: '8px 20px',
                  borderRadius: '10px',
                  bgcolor: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#e72f0d !important' }
                }}
              >
                Odhlásit se
              </Button>
            </>
          )}

          {!showHamburger && !isAuthenticated && (
            <>
              <Button
                onClick={() => { setHeaderAuthMode('login'); setHeaderAuthOpen(true); }}
                sx={{
                  height: '40px',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  color: '#1C1A18',
                  bgcolor: 'transparent',
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: 'rgba(28, 26, 24, 0.05)' }
                }}
              >
                Přihlásit
              </Button>
              <Button
                onClick={() => { setHeaderAuthMode('register'); setHeaderAuthOpen(true); }}
                sx={{
                  height: '40px',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  bgcolor: '#E72F0D !important',
                  color: '#FFFFFF !important',
                  fontSize: '15px',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  boxShadow: '0 6px 18px -8px rgba(231,47,13,0.5)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#c8280b !important', transform: 'translateY(-1px)' }
                }}
              >
                Vyzkoušet zdarma
              </Button>
            </>
          )}

          {/* Hamburger button - shown whenever content doesn't fit */}
          {showHamburger && (
            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="Otevřít menu"
              sx={{ color: '#1F1F1F', ml: 0.5 }}
            >
              <Menu size={24} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '85%',
            maxWidth: 340,
            backgroundColor: '#F9F9F9',
            borderTopLeftRadius: '16px',
            borderBottomLeftRadius: '16px',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Drawer header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src={logoSymbol} alt="Logo" sx={{ width: 32, height: 32 }} />
              <Typography sx={{ fontFamily: '"Rajdhani", sans-serif', fontSize: '22px', fontWeight: 700, color: '#1F1F1F' }}>
                PravniBOT.cz
              </Typography>
            </Box>
            <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#1F1F1F' }}>
              <X size={22} />
            </IconButton>
          </Box>

          {/* Nav items */}
          <Box component="nav" aria-label="Mobilní navigace" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {menuItems.map((item) => {
              const IconComp = item.icon;
              const active = isActive(item.path);
              return (
                <Box
                  key={item.path}
                  onClick={() => handleNavClick(item)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: active ? 'rgba(231, 47, 13, 0.08)' : 'transparent',
                    transition: 'background-color 0.15s',
                    '&:hover': { backgroundColor: active ? 'rgba(231, 47, 13, 0.12)' : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <IconComp size={20} color={active ? '#e72f0d' : '#666'} strokeWidth={1.8} />
                  <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '15px',
                    fontWeight: active ? 600 : 500,
                    color: active ? '#1F1F1F' : '#444',
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Drawer auth buttons */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E5E5E5', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isAuthenticated ? (
              <>
                {user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1F1F1F' }}>{user.username}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                      <Paid sx={{ fontSize: 16, color: '#e72f0d' }} />
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                        {formatCredits(creditBalance)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Button
                  fullWidth
                  onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}
                  startIcon={<DashboardIcon />}
                  sx={{
                    height: 44, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                    fontSize: '14px', bgcolor: '#1F1F1F !important', color: '#fff !important',
                    '&:hover': { bgcolor: '#333 !important' },
                  }}
                >
                  Přehled
                </Button>
                <Button
                  fullWidth
                  onClick={handleLogout}
                  sx={{
                    height: 44, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                    fontSize: '14px', color: '#1F1F1F', border: '1px solid #ddd',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  Odhlásit se
                </Button>
              </>
            ) : (
              <>
                <Button
                  fullWidth
                  onClick={() => { setMobileOpen(false); setHeaderAuthMode('login'); setHeaderAuthOpen(true); }}
                  sx={{
                    height: 44, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                    fontSize: '14px', color: '#1F1F1F', border: '2px solid #1F1F1F',
                    '&:hover': { backgroundColor: 'rgba(31,31,31,0.06)' },
                  }}
                >
                  Přihlásit se
                </Button>
                <Button
                  fullWidth
                  onClick={() => { setMobileOpen(false); setHeaderAuthMode('register'); setHeaderAuthOpen(true); }}
                  sx={{
                    height: 44, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                    fontSize: '14px', bgcolor: '#1F1F1F !important', color: '#fff !important',
                    border: 'none !important',
                    '&:hover': { bgcolor: '#e72f0d !important' },
                  }}
                >
                  Vyzkoušet zdarma
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {headerAuthOpen && (
        <Suspense fallback={null}>
          <AuthModal
            open={headerAuthOpen}
            onClose={() => setHeaderAuthOpen(false)}
            initialMode={headerAuthMode}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default Header;
